// A person's time, day by day, between two dates (time.day), and "Correct hours…" (time.correct):
// the office never edits a tech's taps. A correction is a row of its own with a reason, and a wrong
// one is undone by another with the opposite minutes. A correction in a period that was already
// prepared makes it stale: Lock waits for Prepare again. A day in a locked pay period takes none.
// Owner, manager and bookkeeper only: in Payroll for a period, and in Dispatch › Hours for a week.

import { useState } from 'react';
import { timeText } from '@flowology/kernel';
import { client } from '../api.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { dayText } from './model.ts';
import { endedAtMinutes, minutesText, TYPE_WORD, typedMinutes, type TimeType } from './timesheet-model.ts';
import { Empty, errorText, Loading, Panel, useQuery } from './ui.tsx';
import './timesheet.css';

interface Entry {
  id: string;
  type: TimeType;
  startedAt: string;
  endedAt: string | null;
  minutes: number;
  jobId: string | null;
  jobNumber: string | null;
  nonJobCode: string | null;
  paid: boolean;
  flag: string | null;
  /** The office person who recorded it for the tech ("Dana"); null for the tech's own tap (F-20). */
  recordedBy?: string | null;
}
interface Correction {
  id: string;
  type: TimeType;
  minutes: number;
  paid: boolean;
  reason: string;
  entryId: string | null;
  resolvesFlag: boolean;
  createdAt: string;
}
export interface Day {
  date: string;
  entries: Entry[];
  corrections: Correction[];
  totals: { work: number; drive: number; nonJob: number };
  /** In a pay period whose hours are locked (or sent, or posted): no correction lands on it. */
  locked?: boolean;
}
export interface Staff {
  id: string;
  displayName: string;
  isTechnician: boolean;
}

/** Who corrects hours: time.correct (the owner, the manager and the bookkeeper). */
export const TIME_CORRECT_PERMS = ['time.correct'];

export function Timesheet({
  range,
  timeZone,
  toast,
  stale,
  onStale,
}: {
  /** The pay period's dates. */
  range: { from: string; to: string };
  timeZone: string;
  toast: (m: string) => void;
  /** Hours changed since the period was prepared (the period view's word, the server's). */
  stale: boolean;
  /** A correction in a prepared period: the period view reads it again (it says it's stale). */
  onStale: () => void;
}) {
  const staff = useQuery<{ staff: Staff[] }>('staff.list', { techniciansOnly: true });
  const [staffId, setStaffId] = useState('');
  const person = staff.data?.staff.find((s) => s.id === staffId);
  return (
    <Panel title="Timesheet">
      {!staff.data ? (
        <Loading error={staff.error} what="the technicians" retry={() => void staff.reload()} />
      ) : (
        <>
          <label className="inline-label">
            <span className="flabel">Technician</span>
            <select className="field" value={staffId} onChange={(e) => setStaffId(e.target.value)}>
              <option value="">Pick someone…</option>
              {staff.data.staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName}
                </option>
              ))}
            </select>
          </label>
          {person && (
            <PersonDays
              key={`${person.id}-${range.from}`}
              person={person}
              range={range}
              timeZone={timeZone}
              toast={toast}
              staleNote={
                stale
                  ? 'This pay period was prepared before the change: prepare it again before locking its hours.'
                  : null
              }
              onStale={onStale}
            />
          )}
        </>
      )}
    </Panel>
  );
}

/** One person's days between two dates: their taps, the office's corrections, and the forms to add one. */
export function PersonDays({
  person,
  range,
  timeZone,
  toast,
  staleNote,
  onStale,
  onChanged,
}: {
  person: Staff;
  range: { from: string; to: string };
  timeZone: string;
  toast: (m: string) => void;
  /** Said above the days while the pay period needs preparing again (the screen's words). */
  staleNote: string | null;
  /** A correction landed in a pay period that was already prepared (time.correct's periodStale). */
  onStale: () => void;
  /** Any correction saved: a screen that sums the hours reads them again. */
  onChanged?: () => void;
}) {
  const q = useQuery<{ days: Day[] }>('time.day', { staffId: person.id, from: range.from, to: range.to });
  // A corrected day comes back whole; it replaces the one shown.
  const [changed, setChanged] = useState<Record<string, Day>>({});
  if (!q.data)
    return <Loading error={q.error} what={`${person.displayName}’s time`} retry={() => void q.reload()} />;
  const days = q.data.days.map((d) => changed[d.date] ?? d);
  const saved = (day: Day, periodStale: boolean) => {
    setChanged({ ...changed, [day.date]: day });
    // the period view reads it again, and says it's stale until Prepare (QA-401: never kept here)
    if (periodStale) onStale();
    onChanged?.();
    toast('The correction is saved.');
  };
  return (
    <div className="timesheet">
      {staleNote && (
        <p className="notice warn" role="status">
          {staleNote}
        </p>
      )}
      {days.length === 0 ? (
        <Empty>No time recorded for {person.displayName} in these dates.</Empty>
      ) : (
        days.map((d) => <DayCard key={d.date} day={d} person={person} timeZone={timeZone} onSaved={saved} />)
      )}
    </div>
  );
}

function DayCard({
  day,
  person,
  timeZone,
  onSaved,
}: {
  day: Day;
  person: Staff;
  timeZone: string;
  onSaved: (day: Day, periodStale: boolean) => void;
}) {
  const [open, setOpen] = useState<string | 'add' | null>(null);
  // timesheets are ServiceTitan's while the company mirrors it: corrected there (BL-088)
  const mirroring = useMirror();
  const at = (iso: string) => timeText(iso, timeZone);
  return (
    <section className="stone line-card ts-day" aria-label={dayText(day.date)}>
      <div className="line-top">
        <h3 className="h4">{dayText(day.date)}</h3>
        {day.locked && <span className="chip say">Locked for payroll</span>}
        <span className="muted small">
          Work {minutesText(day.totals.work)} · Drive {minutesText(day.totals.drive)} · Shop{' '}
          {minutesText(day.totals.nonJob)}
        </span>
      </div>
      <ul className="rows ts-rows">
        {day.entries.map((e) => (
          <li key={e.id}>
            <div className="ts-row">
              <span>
                <b>{TYPE_WORD[e.type]}</b> {at(e.startedAt)} – {e.endedAt ? at(e.endedAt) : '…'}
                {e.jobNumber ? ` · #${e.jobNumber}` : ''}
                {!e.paid ? ' · unpaid' : ''}
                {e.recordedBy ? ` · ${e.recordedBy} for ${person.displayName}` : ''}
              </span>
              <span className="num">{minutesText(e.minutes)}</span>
            </div>
            {e.flag && <p className="small ts-flag">{e.flag}</p>}
            {open === e.id ? (
              <CorrectEntry
                entry={e}
                day={day}
                person={person}
                timeZone={timeZone}
                onSaved={(d, s) => {
                  setOpen(null);
                  onSaved(d, s);
                }}
                onCancel={() => setOpen(null)}
              />
            ) : (
              e.flag &&
              !day.locked &&
              !mirroring && (
                <button className="btn quiet" onClick={() => setOpen(e.id)}>
                  Correct hours…
                </button>
              )
            )}
          </li>
        ))}
        {day.corrections.map((c) => (
          <li key={c.id} className="ts-correction">
            <div className="ts-row">
              <span>
                <b>Office correction</b> · {TYPE_WORD[c.type]}: {c.reason}
              </span>
              <span className="num">
                {c.minutes > 0 ? '+' : ''}
                {minutesText(c.minutes)}
              </span>
            </div>
          </li>
        ))}
      </ul>
      {open === 'add' ? (
        <AdjustDay
          day={day}
          person={person}
          onSaved={(d, s) => {
            setOpen(null);
            onSaved(d, s);
          }}
          onCancel={() => setOpen(null)}
        />
      ) : day.locked ? (
        <p className="muted small">Its pay period’s hours are locked: it takes no more corrections.</p>
      ) : mirroring ? (
        <p className="muted small">{MIRROR_LINE}</p>
      ) : (
        <div className="row-actions">
          <button className="btn quiet" onClick={() => setOpen('add')}>
            Add or take off time…
          </button>
        </div>
      )}
    </section>
  );
}

/** "19:00" for an instant, in the company's zone (to compare with a typed end). */
function timeText24(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

async function correct(input: Record<string, unknown>): Promise<{ day: Day; periodStale: boolean }> {
  return client.call<{ day: Day; periodStale: boolean }>('time.correct', input);
}

/** A flagged entry: "Ended at ___" settles it. */
function CorrectEntry({
  entry,
  day,
  person,
  timeZone,
  onSaved,
  onCancel,
}: {
  entry: Entry;
  day: Day;
  person: Staff;
  timeZone: string;
  onSaved: (day: Day, periodStale: boolean) => void;
  onCancel: () => void;
}) {
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const minutes = end ? endedAtMinutes(entry, day.date, end, timeZone) : null;
  // an end before the start is read as the next morning; say so, so a typo isn't a night's work
  const nextMorning = !!end && end < timeText24(entry.startedAt, timeZone);
  const ok = minutes !== null && minutes !== 0 && !!reason.trim();
  // An entry that did end (just flagged) can be settled as it is: 0 minutes, the flag cleared.
  const canConfirm = entry.endedAt !== null && !!reason.trim();
  const send = (change: number) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    correct({
      staffId: person.id,
      localDate: day.date,
      type: entry.type,
      minutes: change,
      reason: reason.trim(),
      entryId: entry.id,
      resolvesFlag: true,
    })
      .then((r) => onSaved(r.day, r.periodStale))
      .catch((x: unknown) => setError(errorText(x)))
      .finally(() => setBusy(false));
  };
  return (
    <form
      className="fgrid inline-form"
      aria-label={`Correct ${TYPE_WORD[entry.type].toLowerCase()} from ${timeText(entry.startedAt, timeZone)}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (ok && minutes !== null) send(minutes);
      }}
    >
      <label>
        <span className="flabel">Ended at</span>
        <input className="field" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
      </label>
      <label className="wide">
        <span className="flabel">Why</span>
        <input
          className="field"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Forgot to end the visit; left site at 4:30"
        />
      </label>
      {minutes !== null && (
        <p className="full muted small">
          {minutes === 0
            ? 'That’s the time it already counts: use “The recorded time is right”.'
            : `${minutes > 0 ? 'Adds' : 'Takes off'} ${minutesText(Math.abs(minutes))} of ${TYPE_WORD[entry.type].toLowerCase()} time${nextMorning ? ', ending after midnight' : ''}.`}
        </p>
      )}
      {error && (
        <p className="full error" role="alert">
          {error}
        </p>
      )}
      <div className="row-actions">
        <button className="btn primary" type="submit" disabled={!ok || busy}>
          {busy ? 'Saving…' : 'Save the correction'}
        </button>
        {entry.endedAt !== null && (
          <button className="btn" type="button" disabled={!canConfirm || busy} onClick={() => send(0)}>
            The recorded time is right
          </button>
        )}
        <button className="btn quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Time the taps missed, or time to take off, for the day. */
function AdjustDay({
  day,
  person,
  onSaved,
  onCancel,
}: {
  day: Day;
  person: Staff;
  onSaved: (day: Day, periodStale: boolean) => void;
  onCancel: () => void;
}) {
  const [type, setType] = useState<TimeType>('work');
  const [sign, setSign] = useState<1 | -1>(1);
  const [hours, setHours] = useState('');
  const [mins, setMins] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const minutes = typedMinutes(hours, mins, sign);
  const ok = minutes !== null && !!reason.trim();
  return (
    <form
      className="fgrid inline-form"
      aria-label={`Change ${dayText(day.date)}’s time`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!ok || busy) return;
        setBusy(true);
        setError(null);
        correct({ staffId: person.id, localDate: day.date, type, minutes, reason: reason.trim() })
          .then((r) => onSaved(r.day, r.periodStale))
          .catch((x: unknown) => setError(errorText(x)))
          .finally(() => setBusy(false));
      }}
    >
      <label>
        <span className="flabel">Time</span>
        <select className="field" value={type} onChange={(e) => setType(e.target.value as TimeType)}>
          <option value="work">Work</option>
          <option value="drive">Drive</option>
          <option value="non_job">Shop</option>
        </select>
      </label>
      <label>
        <span className="flabel">Add or take off</span>
        <select className="field" value={sign} onChange={(e) => setSign(Number(e.target.value) as 1 | -1)}>
          <option value={1}>Add</option>
          <option value={-1}>Take off</option>
        </select>
      </label>
      <label>
        <span className="flabel">Hours</span>
        <input
          className="field qty-in"
          inputMode="numeric"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
        />
      </label>
      <label>
        <span className="flabel">Minutes</span>
        <input
          className="field qty-in"
          inputMode="numeric"
          value={mins}
          onChange={(e) => setMins(e.target.value)}
        />
      </label>
      <label className="wide">
        <span className="flabel">Why</span>
        <input
          className="field"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Shop time not tapped: restocking the truck"
        />
      </label>
      {error && (
        <p className="full error" role="alert">
          {error}
        </p>
      )}
      <div className="row-actions">
        <button className="btn primary" type="submit" disabled={!ok || busy}>
          {busy
            ? 'Saving…'
            : minutes !== null
              ? `${minutes > 0 ? 'Add' : 'Take off'} ${minutesText(Math.abs(minutes))}`
              : 'Save'}
        </button>
        <button className="btn quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
